<div class="component-viewer registration-create">
  <h1 class="mat-h1 page-title">Register golfers</h1>
  <p class="page-subtitle mat-body-1" *ngIf="canRegister && selectedIteration">
    Add players for the selected outing date. Choose whether you are signing up one person, a new team, or someone joining an existing team.
  </p>

  <mat-card *ngIf="canRegister && selectedIteration" class="registration-card">
    <mat-card-header>
      <mat-card-title class="mb-1">Outing registration</mat-card-title>
      <mat-card-subtitle class="mb-1">
        <span class="subtitle-line">
          <mat-icon class="subtitle-icon">event</mat-icon>
          {{ selectedIteration.startDate | date: 'EEEE, MMM d, y' }} at {{ selectedIteration.startDate | date: 'shortTime' }}
        </span>
      </mat-card-subtitle>
    </mat-card-header>

    <mat-divider />

    <mat-card-content>
      <mat-progress-bar mode="indeterminate" *ngIf="isLoading" class="progress-bar" />

      <form [formGroup]="multipleForm" (ngSubmit)="onSubmit(multipleForm)">
        <section class="mode-section" aria-label="Registration type">
          <h2 class="section-heading mat-subtitle-2">How are you registering?</h2>
          <mat-radio-group
            class="mode-radio-group"
            [value]="registrationMode"
            (change)="setRegistrationMode($event.value)"
          >
            <mat-radio-button class="mode-option" value="individual">
              <span class="mode-label">Individual</span>
              <span class="mode-hint">One golfer; they will be recorded as their own team captain.</span>
            </mat-radio-button>
            <mat-radio-button class="mode-option" value="whole_team">
              <span class="mode-label">New team</span>
              <span class="mode-hint">Two to four golfers. Choose who is the team captain.</span>
            </mat-radio-button>
            <mat-radio-button class="mode-option" value="join_team" [disabled]="captainsLoading || teamCaptains.length === 0">
              <span class="mode-label">Join an existing team</span>
              <span class="mode-hint">One golfer joins a team already on this outing (pick the captain).</span>
            </mat-radio-button>
          </mat-radio-group>
          <p class="mat-caption hint-muted" *ngIf="captainsLoading">Loading existing teams…</p>
          <p class="mat-caption hint-muted" *ngIf="!captainsLoading && teamCaptains.length === 0 && registrationMode !== 'join_team'">
            No teams yet for this outing. Use <strong>Individual</strong> or <strong>New team</strong> first, or switch iteration.
          </p>
        </section>

        <ng-container *ngIf="registrationMode === 'join_team'">
          <section class="join-section" *ngIf="teamCaptains.length > 0">
            <h2 class="section-heading mat-subtitle-2">Team captain</h2>
            <mat-form-field appearance="outline" class="full-width">
              <mat-label>Select captain</mat-label>
              <mat-select formControlName="teamCaptain" required>
                <mat-option
                  *ngFor="let t of teamCaptains; trackBy: trackTeamCaptainRow"
                  [value]="t.teamCaptain"
                  [disabled]="t.numberOfGolfers >= 4"
                >
                  {{ t.teamCaptain }} ({{ t.numberOfGolfers }}/4)
                </mat-option>
              </mat-select>
              <mat-hint>Only teams with fewer than four players are listed as available.</mat-hint>
            </mat-form-field>
            <p class="mat-caption warn-text" *ngIf="allTeamsFull">Every team is full for this outing.</p>
          </section>
        </ng-container>

        <section class="golfers-section" aria-label="Golfers">
          <div class="golfers-header">
            <h2 class="section-heading mat-subtitle-2">Golfers</h2>
            <button
              *ngIf="canAddGolfer"
              mat-stroked-button
              type="button"
              color="primary"
              (click)="addRegistration()"
            >
              <mat-icon>person_add</mat-icon>
              Add golfer
            </button>
          </div>

          <mat-radio-group
            *ngIf="registrationMode === 'whole_team'"
            class="captain-radio-group"
            [value]="selectedCaptainIndex"
            (change)="onCaptainRadioChange($event)"
          >
            <ng-container formArrayName="registrations">
              <div
                *ngFor="let registration of registrations().controls; let i = index"
                [formGroupName]="i"
                class="golfer-row golfer-row--captain-pick"
              >
                <div class="captain-cell">
                  <mat-radio-button [value]="i" [disabled]="!golferNamesCompleteAt(i)">
                    Captain
                  </mat-radio-button>
                </div>
                <div class="fields-grid">
                  <mat-form-field appearance="outline" class="full-width">
                    <mat-label>First name</mat-label>
                    <input matInput formControlName="firstName" autocomplete="given-name" />
                    <mat-error *ngIf="registration.get('firstName')?.errors?.required && registration.get('firstName')?.touched">
                      First name is required
                    </mat-error>
                    <mat-error *ngIf="registration.get('firstName')?.errors?.validationErrors">
                      {{ registration.get('firstName')?.errors?.validationErrors }}
                    </mat-error>
                  </mat-form-field>
                  <mat-form-field appearance="outline" class="full-width">
                    <mat-label>Last name</mat-label>
                    <input matInput formControlName="lastName" autocomplete="family-name" />
                    <mat-error *ngIf="registration.get('lastName')?.errors?.required && registration.get('lastName')?.touched">
                      Last name is required
                    </mat-error>
                    <mat-error *ngIf="registration.get('lastName')?.errors?.validationErrors">
                      {{ registration.get('lastName')?.errors?.validationErrors }}
                    </mat-error>
                  </mat-form-field>
                </div>
                <div class="row-actions">
                  <button
                    mat-icon-button
                    type="button"
                    color="warn"
                    *ngIf="canRemoveGolfer"
                    (click)="removeRegistration(i)"
                    aria-label="Remove golfer"
                  >
                    <mat-icon>person_remove</mat-icon>
                  </button>
                </div>
              </div>
            </ng-container>
          </mat-radio-group>

          <ng-container *ngIf="registrationMode !== 'whole_team'" formArrayName="registrations">
            <div
              *ngFor="let registration of registrations().controls; let i = index"
              [formGroupName]="i"
              class="golfer-row"
            >
              <div class="fields-grid">
                <mat-form-field appearance="outline" class="full-width">
                  <mat-label>First name</mat-label>
                  <input matInput formControlName="firstName" autocomplete="given-name" />
                  <mat-error *ngIf="registration.get('firstName')?.errors?.required && registration.get('firstName')?.touched">
                    First name is required
                  </mat-error>
                </mat-form-field>
                <mat-form-field appearance="outline" class="full-width">
                  <mat-label>Last name</mat-label>
                  <input matInput formControlName="lastName" autocomplete="family-name" />
                  <mat-error *ngIf="registration.get('lastName')?.errors?.required && registration.get('lastName')?.touched">
                    Last name is required
                  </mat-error>
                  <mat-error *ngIf="registration.get('lastName')?.errors?.validationErrors">
                    {{ registration.get('lastName')?.errors?.validationErrors }}
                  </mat-error>
                </mat-form-field>
              </div>
            </div>
          </ng-container>
        </section>

        <p class="mat-body-2 warn-text" *ngIf="formError">{{ formError }}</p>

        <mat-card-actions align="end" class="actions-row">
          <button mat-flat-button color="primary" type="submit" [disabled]="submitDisabled">
            Submit registration
          </button>
        </mat-card-actions>
      </form>
    </mat-card-content>
  </mat-card>

  <mat-card *ngIf="!canRegister" class="registration-card">
    <mat-card-content>
      <h2 class="mat-h3">Registration is not available</h2>
      <p class="mat-body-1">
        The outing on <strong>{{ selectedIteration?.startDate | date: 'M/d/yyyy h:mm a' }}</strong> is past its schedule date or registration cannot be opened for it.
      </p>
    </mat-card-content>
  </mat-card>
</div>
